import { TextCycler } from "@/components/status/TextCycler";
import { getBaseThemeName, getConfig, getHazardList } from "@/features/branding";
import { encryptData, encryptSecret, generateRandomSecretKey } from "@/features/cryptography";
import { HacsOmUploadData } from "@/features/hacs";
import { RemoteNewRunModal, RemoteNewRunModalProps } from "@/features/remote_jobs";
import { StoreState } from "@/store";
import { useGetOrgQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { PortfolioExportIdFactory } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportIdFactory";
import PortfolioExportConfigureTemplateForm from "@/tools/aggregate/portfolio-export/components/PortfolioExportConfigureTemplateForm";
import { PortfolioExportErrorAlert } from "@/tools/aggregate/portfolio-export/components/PortfolioExportErrorAlert";
import { PortfolioExportTypeSelection } from "@/tools/aggregate/portfolio-export/components/PortfolioExportTypeSelection";
import { PortfolioExportUploadForm } from "@/tools/aggregate/portfolio-export/components/PortfolioExportUploadForm";
import { PortfolioExportUploadReview } from "@/tools/aggregate/portfolio-export/components/PortfolioExportUploadReview";
import { usePortfolioTutorialsState } from "@/tools/aggregate/portfolio-export/PortfolioTutorialContext";
import { portfolioTypeColours, type PortfolioExportJobSchema, type PortfolioNewRunModalStatus, type PortfolioRegionType } from "@/tools/aggregate/portfolio-export/types";
import { streamToSupabase } from "@/tools/aggregate/portfolio-export/utils/supabase";
import { validateCsvFile } from "@/tools/aggregate/portfolio-export/utils/validation";
import { arrayBufferToString } from "@/utils/crypto";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { ConfigProvider, Timeline } from "antd";
import { RcFile } from "antd/es/upload";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
interface PortfolioExportNewRunModalProps extends RemoteNewRunModalProps<PortfolioExportJobSchema> {
  availablePortfolioTypes: string[];
  creatablePortfolioTypes: string[];
}

/**
 * Provides an interface for a user to setup and  trigger a new Portfolio Export
 * run.
 */
export const PortfolioExportNewRunModal = ({
  availablePortfolioTypes,
  creatablePortfolioTypes,
  ...props
}: PortfolioExportNewRunModalProps) => {
  const router = useRouter();
  const [data, setData] = useState<HacsOmUploadData | undefined>();
  const {
    defaultMetadata
  } = getConfig();
  const hasMultipleCreatableTypes = creatablePortfolioTypes.length > 1;
  const [portfolioType, setPortfolioType] = useState<PortfolioRegionType | undefined>(hasMultipleCreatableTypes ? undefined : creatablePortfolioTypes[0]?.toLowerCase() === "global" ? "global" : "hk");
  const firstStatus = hasMultipleCreatableTypes ? "TYPE_SELECTION" : "INITIAL";
  const [status, setStatus] = useState<PortfolioNewRunModalStatus>(firstStatus);
  const [errors, setErrors] = useState<string[]>([]);
  const [key] = useKeys();
  const {
    data: org
  } = useGetOrgQuery();
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  const tutorialReviewData = {
    file: {
      name: getBaseThemeName() === "hkma" ? "Portfolio HK-locations-3_new" : "Portfolio Example - 3_new_locations"
    },
    lineCount: 10,
    defences: true,
    anomalyManagementRp: true,
    automatedResilience: true
  };
  const {
    newRunModalTypeSelection,
    newRunModalUpload,
    isTutorialNewRunModalOpen,
    tutorialNewRunState
  } = usePortfolioTutorialsState();
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite: false
  });
  useEffect(() => {
    if (tutorialNewRunState && isTutorialNewRunModalOpen) {
      setStatus(tutorialNewRunState);
      setData(tutorialNewRunState === "IN_REVIEW" ? tutorialReviewData as any : undefined);
    }
  }, [tutorialNewRunState, isTutorialNewRunModalOpen]);
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights.newRunModal"
  });
  useEffect(() => {
    if (Object.keys(router.query).includes("handoff")) {
      const data = sessionStorage.getItem("portfolioExportHandoffData");
      if (!data) {
        return;
      }
      const file: any = new File([data], `Handoff Data - ${new Date(Date.now()).toLocaleString()}`, {
        type: "text/csv",
        lastModified: Date.now()
      });
      file["lastModifiedDate"] = new Date();
      file["uid"] = `rc-upload-${Math.floor(Math.random() * 99999999)}-1`;
      setData({
        file: file as RcFile,
        lineCount: data.split("\n").length,
        name: "",
        isPrivate: true,
        defences: true,
        anomalyManagementRp: true,
        automatedResilience: true
      });
      setStatus("IN_REVIEW");
    }
  }, []);
  const validateFile = async (file: RcFile) => {
    if (isTutorialNewRunModalOpen) return;
    setErrors([]);
    const {
      portfolioExport: config
    } = getConfig();
    const portfolioCount = {
      min: org?.is_large_bank && portfolioType === "hk" ? config.largeBank.minAddresses : 0,
      max: config.maxAddresses
    };
    const {
      status,
      messages
    } = await validateCsvFile(file, portfolioCount, portfolioType, creatablePortfolioTypes);
    if (status === "error") {
      setErrors(messages);
      setData(undefined);
      setStatus("INITIAL");
      return;
    }
  };
  const uploadFile = async (file: RcFile) => {
    let succeeded = false;
    try {
      let encryptedSecret = "";
      const idFactory = new PortfolioExportIdFactory();
      const [supabaseStream] = file.stream().tee();
      if (!key.user?.keyPair.publicKey) return;
      const newSecret = await generateRandomSecretKey();
      if (data?.isPrivate) {
        encryptedSecret = arrayBufferToString(await encryptSecret({
          publicKey: key.user.keyPair.publicKey,
          secret: newSecret.exportedKey
        }));
      } else {
        if (key.orgSecret && key.org?.keyPair.publicKey) {
          encryptedSecret = arrayBufferToString(await encryptSecret({
            publicKey: key.org.keyPair.publicKey,
            secret: newSecret.exportedKey
          }));
        }
      }
      const portfolioDetails = {
        name: await encryptData(newSecret.secret, data?.name || removeFileExtension(file.name) || ""),
        groupId: data?.groupId,
        startTime: Date.now(),
        encryptedSecret,
        isPrivate: data?.isPrivate,
        incorporatedHazards: getHazardList().available,
        regionType: portfolioType
      };
      const supabaseResponse = await streamToSupabase({
        stream: supabaseStream,
        idFactory,
        details: portfolioDetails,
        secret: newSecret.secret,
        canRunAutoAdaptation: canRunAutoAdaptation && data?.automatedResilience !== false,
        regionType: portfolioType,
        ...{
          defences: data?.defences ? defaultMetadata.defences : defaultMetadata.defencesDisabled
        },
        ...{
          anomalyManagementRp: data?.anomalyManagementRp ? defaultMetadata.anomalyManagementRp : defaultMetadata.anomalyManagementRpDisabled
        }
      });
      if (supabaseResponse.type === "failure") {
        setErrors(["An error occurred while processing your data. This analysis run will not deduct from your quota. Please contact XDI Support with the time/date of your analysis so we can help troubleshoot this error."]);
        return;
      }
      succeeded = true;

      // We just need to alert down the line that we should close the modal.
      if (props.onCancel) {
        props.onCancel({} as any);
      }
    } catch (error) {
      if (/OperationError/.test(String(error))) {
        setErrors(["The file may contain a line that is currently too long to upload.  This is a known limitation that will be resolved in Q1 2025.", "In the meantime, please ensure addresses follow the format recommendation in the Geocoding section of the Methods documentation (available in the Help menu)."]);
        return;
      } else {
        setErrors(["An error occurred while processing your data. This analysis run will not deduct from your quota. Please contact XDI Support with the time/date of your analysis so we can help troubleshoot this error."]);
        return;
      }
    } finally {
      setStatus("INITIAL");
      if (!succeeded) setData(undefined);
    }
  };
  const totalUploaded = useSelector(({
    portfolioExport
  }: StoreState) => portfolioExport.newRun.totalInSupabase);
  useEffect(() => {
    if (data) {
      switch (status) {
        case "IN_REVIEW":
          validateFile(data.file);
          return;
        case "UPLOADING":
          uploadFile(data.file);
          return;
      }
    }
  }, [data, status]);
  let uploadTitle = t("upload.title");
  if (hasMultipleCreatableTypes && portfolioType !== undefined) {
    uploadTitle = uploadTitle.replace("Portfolio", portfolioType === "hk" ? "HKSAR Portfolio" : "Global Portfolio");
  }
  return <ConfigProvider theme={creatablePortfolioTypes.length > 1 && portfolioType !== undefined ? {
    token: {
      colorPrimary: portfolioTypeColours[portfolioType]
    }
  } : undefined} data-sentry-element="ConfigProvider" data-sentry-component="PortfolioExportNewRunModal" data-sentry-source-file="PortfolioExportNewRunModal.tsx">
      <RemoteNewRunModal title={status === "TYPE_SELECTION" ? "Select Portfolio Type" : status == "CUSTOMISING" ? "Customise Upload Template" : status == "IN_REVIEW" ? t("review.title") : uploadTitle} closable={status !== "UPLOADING"} mask={{
      closable: status === "INITIAL" || status === "TYPE_SELECTION"
    }} width={700} {...props} modalRender={(node: ReactNode) => <div ref={status === "TYPE_SELECTION" ? newRunModalTypeSelection : status === "INITIAL" || status === "CUSTOMISING" ? newRunModalUpload.uploadNewRun : undefined}>
            {node}
          </div>} data-sentry-element="RemoteNewRunModal" data-sentry-source-file="PortfolioExportNewRunModal.tsx">
        <div style={{
        paddingBlockStart: 8
      }}>
          {!!errors.length && <PortfolioExportErrorAlert errors={errors} onClose={() => setErrors([])} />}

          {status === "TYPE_SELECTION" && toolUsage && <PortfolioExportTypeSelection toolUsage={toolUsage} creatablePortfolioTypes={creatablePortfolioTypes} onSelect={type => {
          setPortfolioType(type);
          setStatus("INITIAL");
        }} />}

          {!data && status === "INITIAL" && <PortfolioExportUploadForm data={data} portfolioType={portfolioType} handleGoBack={() => {
          if (creatablePortfolioTypes.length > 1) {
            setErrors([]);
            setStatus("TYPE_SELECTION");
          }
        }} handleCustomise={() => setStatus("CUSTOMISING")} availablePortfolioTypes={availablePortfolioTypes} creatablePortfolioTypes={creatablePortfolioTypes} setData={uploadedData => {
          if (uploadedData) {
            setData({
              ...uploadedData,
              defences: true,
              anomalyManagementRp: true,
              automatedResilience: true
            });
            setStatus("IN_REVIEW");
          } else {
            setData(undefined);
          }
        }} />}

          {!data && status === "CUSTOMISING" && <PortfolioExportConfigureTemplateForm handleDone={() => setStatus("INITIAL")} portfolioType={portfolioType} availablePortfolioTypes={availablePortfolioTypes} />}

          {data && status === "IN_REVIEW" && <PortfolioExportUploadReview data={data} setData={setData} portfolioType={portfolioType} onContinue={() => {
          setStatus("UPLOADING");
        }} onBack={() => {
          setData(undefined);
          setStatus("INITIAL");
        }} isAdvancedSettingsOpen={isTutorialNewRunModalOpen} />}

          {data && status === "UPLOADING" && <div style={{
          padding: "48px 32px 20px 32px",
          borderRadius: 8,
          background: "rgba(0, 0, 0, 0.02)",
          border: "1px solid rgba(217, 217, 217, 0.4)"
        }}>
              <Timeline items={[{
            className: "text-gray-400",
            color: "gray",
            children: <TextCycler messages={["Uploading to secure database"]} intervalMs={5000} />
          }, {
            className: "text-gray-400",
            color: "gray",
            children: <TextCycler messages={["Encrypting client data"]} />
          }, {
            children: `${totalUploaded} encrypted assets uploaded.`
          }]} pending={<strong>Upload in progress. Do not close your browser.</strong>} />
            </div>}
        </div>
      </RemoteNewRunModal>
    </ConfigProvider>;
};